/**
 * AttachmentButton - Trigger file selection for attachments.
 *
 * Features:
 * - Paperclip icon button
 * - Hidden file input
 * - Supports multiple file selection
 * - Shows badge with attachment count
 * - Accessible with keyboard
 */

import React, { useRef } from 'react';
import { Badge, IconButton, Tooltip } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { DEFAULT_UPLOAD_CONFIG } from '../../types/upload';
import { getAcceptAttribute } from '../../utils/fileValidation';

export interface AttachmentButtonProps {
  /** Callback when files are selected */
  onFilesSelected: (files: File[]) => void;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Current number of attachments (for badge) */
  currentCount?: number;
  /** Maximum files allowed */
  maxFiles?: number;
  /** Custom tooltip text */
  tooltipText?: string;
}

export const AttachmentButton: React.FC<AttachmentButtonProps> = ({
  onFilesSelected,
  disabled = false,
  currentCount = 0,
  maxFiles = DEFAULT_UPLOAD_CONFIG.maxFilesPerMessage,
  tooltipText,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAtLimit = currentCount >= maxFiles;
  const buttonDisabled = disabled || isAtLimit;

  const tooltip =
    tooltipText ||
    (isAtLimit
      ? `Maximum ${maxFiles} files reached`
      : currentCount > 0
        ? `Add more files (${currentCount}/${maxFiles})`
        : 'Attach files (images, PDFs, documents)');

  const handleClick = () => {
    if (!buttonDisabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFilesSelected(Array.from(files));
    }
    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={getAcceptAttribute()}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      <Tooltip title={tooltip} arrow>
        <span>
          <IconButton
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            disabled={buttonDisabled}
            size="small"
            aria-label={tooltip}
            sx={{
              color: currentCount > 0 ? 'primary.main' : 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: 'action.hover',
              },
              '&.Mui-disabled': {
                color: 'text.disabled',
              },
            }}
          >
            <Badge
              badgeContent={currentCount > 0 ? currentCount : undefined}
              color="primary"
              max={9}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  height: 16,
                  minWidth: 16,
                },
              }}
            >
              <AttachFileIcon fontSize="small" />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
    </>
  );
};

export default AttachmentButton;
